import { useEffect, type ReactNode } from 'react';

/**
 * Asking "are you sure?" properly.
 *
 * The browser's own confirm box is a grey strip at the top of the screen with
 * the site's address on it — it looks like a warning about the website rather
 * than a question from the shop's software, and it cannot show the figures that
 * make the question answerable.
 *
 * This asks in the middle of the screen, in the shop's own type, and has room
 * for what actually matters: the amount, the customer, and what the action will
 * do that cannot be undone. Escape cancels, so a wrong click costs nothing.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  detail,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  busy,
  confirmDisabled,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  /** The figures behind the question — an amount, a count, a name. */
  detail?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  busy?: boolean;
  /** Hold the confirm button until the form inside is valid. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter' && !busy && !confirmDisabled) onConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, confirmDisabled, onCancel, onConfirm]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center bg-rail/50 p-4 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-xl bg-surface shadow-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`h-1.5 ${tone === 'danger' ? 'bg-danger' : 'bg-velvet'}`} />
        <div className="px-6 py-5">
          <h2 className="text-lg font-semibold">{title}</h2>
          <div className="mt-1 text-sm text-ink-soft">{message}</div>
          {detail ? (
            <div className="mt-4 rounded-lg border border-silver-100 bg-silver-50 px-4 py-3 text-sm">{detail}</div>
          ) : null}
        </div>
        <div className="flex gap-2 border-t border-silver-100 bg-silver-50/60 px-6 py-3">
          <button className="btn-ghost flex-1" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            className={`flex-1 ${tone === 'danger' ? 'btn-danger' : 'btn-primary'}`}
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
            autoFocus
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
